/**
 * Pure display helpers for student-housing fields on application detail v2.
 * Mirrors tenant Review cards (PropertyReviewCard + StudentEmploymentFields).
 */
import { parseStudentFinancialAssistance } from '#app/components/screening/student/StudentEmploymentFields'
import type {
	EmploymentStepData,
	PropertyStepData,
	VerifyIncomeStepData,
} from './step-data-schemas'

const OCCUPANT_FIELD_ORDER = [
	'firstName',
	'lastName',
	'email',
	'phone',
	'relationship',
	'dateOfBirth',
	'gender',
	'schoolProgram',
	'studyLevelAndGraduation',
] as const

const OCCUPANT_FIELD_LABELS: Record<string, string> = {
	firstName: 'First Name',
	lastName: 'Last Name',
	email: 'Email',
	phone: 'Phone',
	relationship: 'Relationship',
	dateOfBirth: 'Date Of Birth',
	gender: 'Gender',
	schoolProgram: 'School & Program',
	studyLevelAndGraduation: 'Study Level & Graduation',
}

export function occupantFieldLabel(key: string): string {
	return (
		OCCUPANT_FIELD_LABELS[key] ??
		key
			.replace(/([A-Z])/g, ' $1')
			.replace(/^./, (c) => c.toUpperCase())
			.trim()
	)
}

export function sortedOccupantFieldKeys(
	adult: Record<string, unknown>,
): string[] {
	const exclude = new Set(['id', 'name', 'phoneCountryCode'])
	const keys = Object.keys(adult).filter((k) => !exclude.has(k))
	return keys.sort((a, b) => {
		const ai = OCCUPANT_FIELD_ORDER.indexOf(
			a as (typeof OCCUPANT_FIELD_ORDER)[number],
		)
		const bi = OCCUPANT_FIELD_ORDER.indexOf(
			b as (typeof OCCUPANT_FIELD_ORDER)[number],
		)
		if (ai !== -1 && bi !== -1) return ai - bi
		if (ai !== -1) return -1
		if (bi !== -1) return 1
		return a.localeCompare(b)
	})
}

export function linkedOccupantLabels(
	linkedIds: string[],
	adults: Array<{ id?: string; firstName?: string; lastName?: string }>,
): string {
	return linkedIds
		.map((id) => {
			const idx = adults.findIndex((a) => a.id === id)
			if (idx < 0) return id
			const name =
				`${adults[idx]?.firstName ?? ''} ${adults[idx]?.lastName ?? ''}`.trim()
			return `Occupant ${idx + 1}${name ? ` (${name})` : ''}`
		})
		.join(', ')
}

export type StudentFinancialRow = {
	label: string
	type: string
	grossIncome: string
	frequency: string
}

export function deriveStudentFinancialRows(
	employment: EmploymentStepData | null | undefined,
): StudentFinancialRow[] {
	if (!employment) return []
	const rows = parseStudentFinancialAssistance(
		employment as Parameters<typeof parseStudentFinancialAssistance>[0],
	)
	return rows.map((row, i) => ({
		label: `Assistance ${rows.length > 1 ? i + 1 : ''}`.trim(),
		type: row.type,
		grossIncome: row.grossIncome,
		frequency: row.frequency,
	}))
}

export type IncomeVerificationDisplay = {
	canadianBankAccount: string | null
	verifyIncomePath: string | null
}

export function deriveIncomeVerificationDisplay(
	employment: EmploymentStepData | null | undefined,
	verifyIncome: VerifyIncomeStepData | null | undefined,
): IncomeVerificationDisplay {
	const bank =
		(typeof employment?.hasCanadianBankAccount === 'string'
			? employment.hasCanadianBankAccount
			: typeof verifyIncome?.hasCanadianBankAccount === 'string'
				? verifyIncome.hasCanadianBankAccount
				: null) ?? null

	let path: string | null = null
	if (verifyIncome?.skipped === true) {
		if (verifyIncome.skipReason === 'no_canadian_bank_account') {
			path = 'Skipped — no Canadian bank account'
		} else {
			path = 'Skipped'
		}
	} else if (verifyIncome?.completed === true || verifyIncome?.flinksRecordId) {
		path = 'Completed via bank connection'
	}

	return {
		canadianBankAccount: bank
			? bank === 'yes'
				? 'Yes'
				: bank === 'no'
					? 'No'
					: bank
			: null,
		verifyIncomePath: path,
	}
}

export function studentRentalShowsEvictionQuestion(status: string): boolean {
	return status === 'currently-renting' || status === 'previously-renting'
}

export function hasStudentHousingDetailContent(input: {
	property: PropertyStepData | null | undefined
	employment: EmploymentStepData | null | undefined
	verifyIncome: VerifyIncomeStepData | null | undefined
}): boolean {
	const adults = input.property?.adults ?? []
	const guarantors = input.property?.guarantors ?? []
	const financial = deriveStudentFinancialRows(input.employment)
	const income = deriveIncomeVerificationDisplay(
		input.employment,
		input.verifyIncome,
	)
	const emp = input.employment
	return (
		adults.length > 0 ||
		guarantors.length > 0 ||
		Boolean(emp?.immigrationStatus) ||
		financial.length > 0 ||
		emp?.receivingParentalAssistance === 'yes' ||
		Boolean(emp?.parentalAssistanceSource) ||
		Boolean(income.canadianBankAccount) ||
		Boolean(income.verifyIncomePath)
	)
}
